import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Skeleton } from '../../../components/ui/Skeleton';
import { useInvestmentConfig, useUpdateInvestmentConfig } from '../../../hooks/useInvestments';

export function InvestmentConfig() {
  const { data: config, isLoading } = useInvestmentConfig();
  const updateConfig = useUpdateInvestmentConfig();
  
  const [formData, setFormData] = useState({
    custodianType: '',
    minApplicationAmount: 100,
    maxApplicationAmount: 0,
    suitabilityRequired: true,
    suitabilityValidityDays: 730,
    administrationFeeRate: 0,
    isEnabled: false,
  });

  const [isEditing, setIsEditing] = useState(false);

  const handleEdit = () => {
    if (config) {
      setFormData({
        custodianType: config.custodianType || '',
        minApplicationAmount: config.minApplicationAmount,
        maxApplicationAmount: config.maxApplicationAmount || 0,
        suitabilityRequired: config.suitabilityRequired,
        suitabilityValidityDays: config.suitabilityValidityDays,
        administrationFeeRate: config.administrationFeeRate,
        isEnabled: config.isEnabled,
      });
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    await updateConfig.mutateAsync({
      ...formData,
      minApplicationAmount: formData.minApplicationAmount * 100,
      maxApplicationAmount: formData.maxApplicationAmount ? formData.maxApplicationAmount * 100 : undefined,
      administrationFeeRate: formData.administrationFeeRate / 100,
    });
    setIsEditing(false);
  };

  const handleToggleModule = async () => {
    await updateConfig.mutateAsync({ isEnabled: !config?.isEnabled });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Configuração de Investimentos</h1>
          <p className="text-gray-500">Configure o marketplace de investimentos</p>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant={config?.isEnabled ? 'success' : 'default'}>
            {config?.isEnabled ? 'Ativo' : 'Inativo'}
          </Badge>
          <Button
            variant={config?.isEnabled ? 'secondary' : 'primary'}
            onClick={handleToggleModule}
            disabled={updateConfig.isPending}
          >
            {config?.isEnabled ? 'Desativar Módulo' : 'Ativar Módulo'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Custodiante</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <div>
                <label className="block text-sm font-medium mb-1">Tipo de Custodiante</label>
                <Input
                  value={formData.custodianType}
                  onChange={(e) => setFormData({ ...formData, custodianType: e.target.value })}
                  placeholder="Ex: BTG, XP, CETIP"
                />
              </div>
            ) : (
              <div>
                <span className="text-sm text-gray-500">Tipo de Custodiante</span>
                <p className="font-medium">{config?.custodianType || 'Não configurado'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Limites de Aplicação</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">Valor Mínimo (R$)</label>
                  <Input
                    type="number"
                    value={formData.minApplicationAmount}
                    onChange={(e) => setFormData({ ...formData, minApplicationAmount: parseFloat(e.target.value) })}
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Valor Máximo (R$) - 0 = sem limite</label>
                  <Input
                    type="number"
                    value={formData.maxApplicationAmount}
                    onChange={(e) => setFormData({ ...formData, maxApplicationAmount: parseFloat(e.target.value) })}
                    min={0}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <span className="text-sm text-gray-500">Valor Mínimo</span>
                  <p className="font-medium">{formatCurrency(config?.minApplicationAmount || 0)}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Valor Máximo</span>
                  <p className="font-medium">
                    {config?.maxApplicationAmount 
                      ? formatCurrency(config.maxApplicationAmount) 
                      : 'Sem limite'}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Suitability (Perfil do Investidor)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.suitabilityRequired}
                    onChange={(e) => setFormData({ ...formData, suitabilityRequired: e.target.checked })}
                    className="rounded"
                  />
                  <span>Exigir preenchimento do questionário</span>
                </label>
                <div>
                  <label className="block text-sm font-medium mb-1">Validade do Perfil (dias)</label>
                  <Input
                    type="number"
                    value={formData.suitabilityValidityDays}
                    onChange={(e) => setFormData({ ...formData, suitabilityValidityDays: parseInt(e.target.value) })}
                    min={30}
                    max={3650}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span>Questionário Obrigatório</span>
                  <Badge variant={config?.suitabilityRequired ? 'success' : 'default'}>
                    {config?.suitabilityRequired ? 'Sim' : 'Não'}
                  </Badge>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Validade do Perfil</span>
                  <p className="font-medium">{config?.suitabilityValidityDays} dias</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Taxas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <div>
                <label className="block text-sm font-medium mb-1">Taxa de Administração (%)</label>
                <Input
                  type="number"
                  value={formData.administrationFeeRate}
                  onChange={(e) => setFormData({ ...formData, administrationFeeRate: parseFloat(e.target.value) })}
                  min={0}
                  max={100}
                  step={0.01}
                />
              </div>
            ) : (
              <div>
                <span className="text-sm text-gray-500">Taxa de Administração</span>
                <p className="font-medium">{config?.administrationFeeRate?.toFixed(2)}%</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end gap-4">
        {isEditing ? (
          <>
            <Button variant="secondary" onClick={() => setIsEditing(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={updateConfig.isPending}>
              {updateConfig.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </>
        ) : (
          <Button onClick={handleEdit}>Editar Configurações</Button>
        )}
      </div>
    </div>
  );
}

export default InvestmentConfig;
